// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, no-constant-binary-expression, react-hooks/exhaustive-deps */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import PropTypes from "prop-types";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Form, Grid, Icon, Loader, Popup, Segment } from "semantic-ui-react";
import * as protocolApi from "../../../../apis/protocolApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import * as analysisActions from "../../../../redux/actions/analysisActions";
import * as statisticsActions from "../../../../redux/actions/statisticsActions";
import * as trialActions from "../../../../redux/actions/trialActions";
import DataAnalysisCriteria from "../../../Lumber/DataAnalysisCriteria";
import MetaTagsTable from "./MetaTagsTable.tsx";
import ProtocolDataSummaryTable from "./ProtocolDataSummaryTable";
import ProtocolSiteSpecificSummaryTable from "./ProtocolSiteSpecificSummaryTable";
import ProtocolSummaryTableEditor from "./ProtocolSummaryTableEditor.tsx";
import { tooltipDefaults } from "./constants.ts";

const ProtocolSummaryTable = ({
	assessmentOptions,
	dataNormalizationOptions,
	trialIds,
	treatmentData,
	dashboardOptions,
	meanComparisonOptions,
	alphaOptions,
	presetOptions,
	defaultMeanComparisonOption,
	defaultAlphaOption,
	presetData,
	setIsDirty,
	setSaveAction,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const getProtocolTrialFilters = async (clientId) => {
		const accessToken = await getTokenSilently();
		const json = protocolApi.getProtocolTrialFilters(clientId, accessToken);
		return json;
	};

	const { isLoading: trialFiltersLoading, data: protocolTrialFilters } = useQuery({
		queryKey: ["protocol trial filters", userAuth.currentClientId],
		queryFn: () => getProtocolTrialFilters(userAuth.currentClientId),
		select: (data) => data.data,
	});

	const updateTrialsMetaTags = async (updateInfo) => {
		const accessToken = await getTokenSilently();
		return protocolApi.updateTrialsMetaTags(updateInfo, userAuth.currentClientId, accessToken);
	};

	const { mutate: updateMetaTags } = useMutation({
		mutationFn: (updateInfo) => updateTrialsMetaTags(updateInfo),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["meta factors", userAuth.currentClientId] });
			toast.success("Meta Tags updated successfully!");
		},
		onError: (error) => {
			toast.error("Meta Tags failed to update.");
			console.error(error);
		},
	});

	//-- UI Control

	// configuredAssessments is a state to hold the list of assessments configured for the protocol
	// summary table. Each assessment includes properties like assessmentId, assessmentName,
	// dataNormalizationId, etc. This is initialized in the useEffect below by filtering curve
	// models/stand counts from assessmentOptions and ground data assessments from dashboardOptions,
	// applying default data normalization settings. Assessments have a checked property which
	// designates whether the assesment is selected for viewing in the Summary Table.
	const [configuredAssessments, setConfiguredAssessments] = useState(null);

	const [combinedAssessmentOptions, setCombinedAssessmentOptions] = useState(null);
	const [selectedCombinedAssessmentOption, setSelectedCombinedAssessmentOption] = useState(null);
	const [trialsChecked, setTrialsChecked] = useState(null);
	const [initTrialsChecked, setInitTrialsChecked] = useState({});
	const [compiledData, setCompiledData] = useState([]);
	const [compiledTukeyData, setCompiledTukeyData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [treatmentsChecked, setTreatmentsChecked] = useState(null);
	const [selectedMeanComparisonOption, setSelectedMeanComparisonOption] = useState(defaultMeanComparisonOption);
	const [selectedAlphaOption, setSelectedAlphaOption] = useState(defaultAlphaOption);
	const [selectedAssessmentPresetOption, setSelectedAssessmentPresetOption] = useState(null);
	const [compiledMeanComparisonsRunning, setCompiledMeanComparisonsRunning] = useState(true);
	const [singleSiteTukeyRunning, setSingleSiteTukeyRunning] = useState(true);
	const [isUpdatingChange, setIsUpdatingChange] = useState(true);
	const [initialTreatmentCheckedList, setInitialTreatmentCheckedList] = useState([]);
	const [hideUncheckedTreatmentData, setHideUncheckedTreatmentData] = useState(false);
	const [hideLetters, setHideLetters] = useState(false);
	const [didCheckedAssessmentsChange, setDidCheckedAssessmentsChange] = useState(false);
	const [isEditingMetadata, setIsEditingMetadata] = useState(false);
	const [noData, setNoData] = useState(false);
	const [trialFilter, setTrialFilter] = useState({
		value: "",
		filters: {
			quality: [],
			status: [],
			response: [],
		},
	});

	const [openModalEditor, setOpenModalEditor] = useState(false);

	const trialFilterOptions = useMemo(() => {
		const opts =
			protocolTrialFilters?.map((opt) => ({
				key: opt.id,
				text: opt.name,
				filters: {
					quality: opt.trialDataQualityIds,
					status: opt.trialExclusionStatusIds,
					response: opt.trialSiteResponseIds,
				},
				value: opt.id,
			})) ?? [];
		opts.push({
			key: "custom",
			text: "Custom",
			filters: {},
			value: "custom",
		});

		return opts;
	}, [protocolTrialFilters]);

	//-- Data Sources
	const reduxProtocolData = useSelector((state) => (state.protocolData ? state.protocolData : null));
	const [rawTrialData, setRawTrialData] = useState(null);
	const [siteSpecificData, setSiteSpecificData] = useState(null);
	const [trialData, setTrialData] = useState(null);
	const [singleTrialAnovaValues, setSingleTrialAnovaValues] = useState([]);

	//Data Analysis Criteria
	const [needToUpdateAnalytics, setNeedToUpdateAnalytics] = useState(false);
	const [removeDamagedOrExcluded, setRemoveDamagedOrExcluded] = useState(true);
	const [removeOutliers, setRemoveOutliers] = useState(false);
	const [outlierType, setOutlierType] = useState(3);

	const filteredSiteSpecificData = useMemo(
		() =>
			_.filter(siteSpecificData, (ssd) => {
				return ssd.iscurvemodel == String(true) || ssd.isStandCount == String(true)
					? ssd.assessmentId == selectedCombinedAssessmentOption.assessmentid
					: ssd.assessmentId == selectedCombinedAssessmentOption.assessmentid &&
							ssd.plannedTimingId == selectedCombinedAssessmentOption.plannedtimingid;
			}),
		[siteSpecificData, selectedCombinedAssessmentOption],
	);

	useEffect(() => {
		if (presetData) {
			setSelectedAssessmentPresetOption(_.find(presetData, { default: true })?.id ?? presetOptions[0]?.key);
		}
	}, [presetData]);

	const trialInfo = useMemo(
		() =>
			_.sortBy(
				_.map(
					_.filter(trialData, (td) =>
						_.map(_.uniqBy(filteredSiteSpecificData, "trialId"), "trialId").includes(td.trialInfo.trialId),
					),
					(td) => td.trialInfo,
				),
				["farmCountry", "farmRegion", "farmCity", "trialName", "cooperatorName", "trialId"],
			),
		[trialData, filteredSiteSpecificData],
	);

	useEffect(() => {
		getTrialData();
	}, []);

	useEffect(() => {
		initializeTreatmentsChecked();
	}, [treatmentData, trialIds]);

	useEffect(() => {
		if (trialData && trialFilter?.value && trialFilter.value !== "custom") {
			for (let { trialInfo } of trialData) {
				if (
					!trialFilter.filters.status.includes(trialInfo.trialExclusionStatusId) &&
					!trialFilter.filters.response.includes(trialInfo.trialSiteResponseId) &&
					!trialFilter.filters.quality.includes(trialInfo.trialDataQualityId)
				) {
					updateCheckedTrial(true, trialInfo.trialId, true, true);
				} else {
					updateCheckedTrial(false, trialInfo.trialId, true, true);
				}
			}
		}
	}, [trialFilter, trialData, compiledMeanComparisonsRunning]);

	useEffect(() => {
		if (treatmentsChecked && trialsChecked) {
			updateCheckedSiteData();
		}
	}, [trialsChecked]);

	useEffect(() => {
		if (siteSpecificData?.length > 0 && isUpdatingChange) {
			updateCompiledData(false);
		}
	}, [isUpdatingChange, siteSpecificData]);

	useEffect(() => {
		if (configuredAssessments && rawTrialData == null) {
			let rawTrialDataByReference = [];
			getAnalysisResults(rawTrialDataByReference);
		}
		setHideLetters(true);
	}, [configuredAssessments, rawTrialData]);

	useEffect(() => {
		if (rawTrialData && reduxProtocolData && configuredAssessments && trialData) {
			if (!trialsChecked) {
				initializeTrialsChecked();
			}
			let tempRequiredLength = configuredAssessments.length;
			if (rawTrialData.length == tempRequiredLength) {
				calculateSiteData();
			}
		}
	}, [rawTrialData, reduxProtocolData, trialData]);

	useEffect(() => {
		if (reduxProtocolData) {
			if (!defaultMeanComparisonOption) {
				setSelectedMeanComparisonOption(reduxProtocolData?.meanComparisonId ?? meanComparisonOptions[0]?.key);
			}

			if (!defaultAlphaOption) {
				setSelectedAlphaOption(reduxProtocolData?.alphaId ?? alphaOptions[0]?.key);
			}
		}
	}, [reduxProtocolData]);

	// Effect to initialize setConfiguredAssessments when assessmentOptions or dashboardOptions
	// change. Filters and maps assessments to include configuration details like normalization,
	// visibility, and types. Also builds combinedAssessmentOptions for the dropdown and sets the
	// default selectedCombinedAssessmentOption.
	useEffect(() => {
		if (assessmentOptions && dashboardOptions) {
			let tempSelectedAssessmentOptions = [];
			let tempCombinedAssessmentOptions = [];
			let defaultDataNormalizationOption =
				_.find(dataNormalizationOptions, { text: "% MEAN" }) ?? dataNormalizationOptions[0];
			const defaultDataNormalizationOptionAlternate = _.find(
				dataNormalizationOptions,
				{ text: "NONE" } ?? dataNormalizationOptions[0],
			);
			_.map(
				_.filter(assessmentOptions, (assessmentOption) => {
					//ignore all assessments other than curve models
					return assessmentOption.iscurvemodel == String(true) || assessmentOption.isStandCount == String(true);
				}),
				(ao) => {
					tempSelectedAssessmentOptions.push({
						assessmentId: ao.key,
						assessmentName: ao.text,
						plannedTimingId: null,
						plannedTimingName: ao.plannedTimingName, // this is actually "curve model timing name"
						dataNormalizationId: defaultDataNormalizationOption.key,
						dataNormalizationName: defaultDataNormalizationOption.text,
						isgrounddata: ao.isgrounddata,
						iscurvemodel: ao.iscurvemodel,
						isStandCount: ao.isStandCount ?? "false",
						unitName: ao.unitName,
						curveModelAnalysisId: ao.curvemodelanalysisid,
						analysisId: ao.analysisid,
						analysisTypeId: ao.analysistypeid,
						visible: true,
						curveModelId: ao.curveModelId,
					});

					const guid = crypto.randomUUID();
					tempCombinedAssessmentOptions.push({
						key: guid,
						value: guid,
						text: ao.text,
						assessmentid: ao.key,
						plannedtimingid: null,
						analysistypeid: ao.analysistypeid,
						curveModelId: ao.curveModelId,
					});
				},
			);
			_.map(dashboardOptions.groundDataAssessments, (gda) => {
				// set data normalization option to NONE for all ground data
				const { key: dataNormalizationId, text: dataNormalizationName } = defaultDataNormalizationOptionAlternate;
				tempSelectedAssessmentOptions.push({
					assessmentId: gda.groundDataAssessmentId,
					assessmentName: gda.groundDataAssessmentName,
					plannedTimingId: gda.groundDataGrowthPhaseId,
					plannedTimingName: gda.groundDataGrowthPhaseName,
					dataNormalizationId,
					dataNormalizationName,
					isgrounddata: true,
					iscurvemodel: false,
					isStandCount: false,
					unitName: gda.unitName,
					curveModelAnalysisId: null,
					analysisId: null,
					analysisTypeId: null,
					visible: true,
				});

				const guid = crypto.randomUUID();
				//temp combined assessment options is for the site specific table
				tempCombinedAssessmentOptions.push({
					key: guid,
					value: guid,
					text: gda.groundDataAssessmentName + ", " + gda.groundDataGrowthPhaseName,
					assessmentid: gda.groundDataAssessmentId,
					plannedtimingid: gda.groundDataGrowthPhaseId,
				});
			});
			setConfiguredAssessments(tempSelectedAssessmentOptions);
			setCombinedAssessmentOptions(tempCombinedAssessmentOptions);

			let defaultAssessmentOption = _.find(tempCombinedAssessmentOptions, (t) => t.text.includes("Total Plant Health"));

			setSelectedCombinedAssessmentOption(defaultAssessmentOption ?? tempCombinedAssessmentOptions[0]);
		}
	}, [assessmentOptions, dashboardOptions]);

	useEffect(() => {
		if (selectedCombinedAssessmentOption && !compiledMeanComparisonsRunning) {
			getSingleTrialAucMeanComparisonData();
		}
	}, [selectedCombinedAssessmentOption]);

	useEffect(() => {
		if (selectedAssessmentPresetOption) {
			updateAssessmentsWithPreset();
		}
	}, [selectedAssessmentPresetOption]);

	function initializeTreatmentsChecked() {
		const tChecked = _.map(treatmentData, (td) => {
			return { trialTreatmentId: td.trialTreatmentId, treatmentChecked: true, dirty: false };
		});
		setTreatmentsChecked(tChecked);
	}

	function initializeTrialsChecked() {
		const tChecked = _.map(trialData, (t) => {
			return { trialId: t.trialInfo.trialId, fieldId: t.trialInfo.fieldId, trialChecked: true };
		});
		setTrialsChecked(tChecked);
		setTrialFilter(trialFilterOptions.find((opt) => opt.text === "Remove Excluded Trials"));
	}

	//-- this function gets the data that is used to build out the site specific components
	async function getTrialData() {
		const accessToken = await getTokenSilently();
		dispatch(trialActions.getMetadataForTrials(trialIds, userAuth.currentClientId, accessToken))
			.then((res) => {
				//split data up per trial
				setTrialData(res);
			})
			.catch((err) => {
				console.log(err);
				setLoading(false);
			});
	}

	async function getAnalysisResults(rawTrialDataByReference) {
		const accessToken = await getTokenSilently();
		let standCountAnalysisId = _.find(configuredAssessments, (sao) => {
			return sao.assessmentName.includes("Stand Count (LAI)");
		});
		if (!needToUpdateAnalytics || hideLetters) {
			setLoading(true);
		}
		await Promise.all(
			_.map(trialIds, (trialId) => {
				return dispatch(
					analysisActions.GetPlotAnalysisResultsForTrialForProtocolSummaryTable(
						userAuth.currentClientId,
						trialId,
						accessToken,
						removeOutliers,
						removeDamagedOrExcluded,
						outlierType,
						standCountAnalysisId ? standCountAnalysisId?.analysisId : "",
					),
				).then((res) => {
					return res.data;
				});
			}),
		)
			.then((res) => {
				let condensedResponse = [];
				_.map(res, (r) => {
					_.map(r, (ir) => {
						condensedResponse.push(ir);
					});
				});
				let filteredTreatments = [];
				_.map(configuredAssessments, (sao) => {
					let f = _.filter(condensedResponse, (cr) => {
						return (
							cr.trialTreatmentId <= reduxProtocolData.treatments &&
							cr.excludeFromAssessment === false &&
							(cr.curveModelName == sao.assessmentId ||
								(cr.assessmentId == sao.assessmentId && cr.plannedTimingId == sao.plannedTimingId) ||
								(sao.isStandCount === String(true) && sao.analysisId === cr.analysisId))
						);
					});
					filteredTreatments.push(f);
				});

				rawTrialDataByReference.push(...filteredTreatments);
				let tempRequiredLength = configuredAssessments.length;
				if (rawTrialDataByReference.length === tempRequiredLength) {
					setRawTrialData(rawTrialDataByReference);
					setIsUpdatingChange(true);
				}
			})
			.catch((err) => {
				console.log(err);
				setLoading(false);
				toast.error("Failed to load trial data.");
			});
	}

	function calculateSiteData() {
		let siteData = [];
		let trialTreatmentAllFailures = [];
		_.map(configuredAssessments, (sao) => {
			const assessment = _.find(assessmentOptions, (ao) => {
				return ao.value === sao.assessmentId;
			});
			let filteredTrialData = _.filter(rawTrialData, (rtd) => {
				return (
					rtd.length > 0 &&
					(sao.isStandCount == String(true)
						? rtd[0].analysisId == sao.assessmentId
						: sao.iscurvemodel == String(true)
							? rtd[0].curveModelName == sao.assessmentId
							: rtd[0].assessmentId == sao.assessmentId && rtd[0].plannedTimingId == sao.plannedTimingId)
				);
			});

			_.map(filteredTrialData, (td) => {
				td = _.filter(td, (d) => !d.failed || (d.curveModelName !== null && d.curveModelName?.includes("AUC")));
				let uniqueTrials = _.uniqBy(td, "trialId");
				//loop over unique trials and return entries from td associated to each trial
				_.map(uniqueTrials, (trial) => {
					//-- Insert new record for failure tracking
					trialTreatmentAllFailures.push({ trialId: trial.trialId, assessmentId: sao.assessmentId, failed: true });

					let filteredTd = _.filter(td, { trialId: trial.trialId });
					const dataNormalization = getDataNormalizationModifier(filteredTd, sao.assessmentId, sao.dataNormalizationId);
					let trialDataTemp = [];
					for (var i = 1; i <= reduxProtocolData.treatments; ++i) {
						var trialTreatmentData = _.map(
							_.filter(filteredTd, (trial) => {
								return (
									trial.trialTreatmentId === i &&
									trial.excluded !== true &&
									(trial.curveModelName == sao.assessmentId ||
										trial.assessmentId == sao.assessmentId ||
										(sao.isStandCount == String(true) && trial.analysisId == sao.assessmentId)) &&
									(trial.groundDatasetAssessmentValue !== null ||
										trial.curveModelAnalysisValue !== null ||
										trial.standCountAnalysisResultValue !== null)
								);
							}),
							assessment.isStandCount === String(true)
								? "standCountAnalysisResultValue"
								: assessment.isgrounddata == String(true)
									? "groundDatasetAssessmentValue"
									: "curveModelAnalysisValue",
						);

						var trialTreatmentPlotData = _.map(
							_.filter(filteredTd, (trial) => {
								return (
									trial.trialTreatmentId === i &&
									trial.excluded !== true &&
									(trial.curveModelName == sao.assessmentId ||
										trial.assessmentId == sao.assessmentId ||
										(sao.isStandCount == String(true) && trial.analysisId == sao.assessmentId)) &&
									(trial.groundDatasetAssessmentValue !== null ||
										trial.curveModelAnalysisValue !== null ||
										trial.standCountAnalysisResultValue !== null)
								);
							}),
							(d) => {
								return {
									plotReplicate: d.plotReplicate,
									plotId: d.plotId,
									fieldId: d.fieldId,
									analysisValue:
										((assessment.isStandCount === String(true)
											? d.standCountAnalysisResultValue
											: assessment.isgrounddata == String(true)
												? d.groundDatasetAssessmentValue
												: d.curveModelAnalysisValue) /
											dataNormalization) *
										getDataNormalizationMultiplier(sao.dataNormalizationId),
									trialTreatmentId: d.trialTreatmentId,
									assessmentId:
										assessment.isStandCount == String(true)
											? d.analysisId
											: assessment.isgrounddata == String(true)
												? d.assessmentId
												: d.curveModelName,
									trialId: d.trialId,
									dataNormalizationId: sao.dataNormalizationId,
								};
							},
						);

						const trialMetaData = trialData.find((tdata) => tdata.trialInfo.trialId === trial.trialId).trialInfo;

						if (filteredTd?.length > 0) {
							let siteObject = {
								trialId: filteredTd[0].trialId,
								trialName: filteredTd[0].trialName,
								cooperatorName: trialMetaData.cooperatorName,
								trialTreatmentId: i,
								assessmentId: sao.assessmentId,
								assessmentName: sao.assessmentName,
								iscurvemodel: sao.iscurvemodel,
								isStandCount: sao.isStandCount,
								unitName: filteredTd[0].unitName,
								plannedTimingId: sao.plannedTimingId,
								average: 0,
								excluded: true,
								region: filteredTd[0].region,
								country: filteredTd[0].country,
								countryCode: filteredTd[0].countryCode,
								city: filteredTd[0].city,
								fieldId: filteredTd[0].fieldId,
								plots: trialTreatmentPlotData,
								trialChecked: trialsChecked
									? trialsChecked.find((tc) => tc.trialId === filteredTd[0].trialId).trialChecked
									: true,
								treatmentChecked: treatmentsChecked
									? treatmentsChecked.find((tc) => tc.trialTreatmentId === i).treatmentChecked
									: true,
								trialDataQualityId: trialMetaData.trialDataQualityId,
								trialExclusionStatusId: trialMetaData.trialExclusionStatusId,
								trialSiteResponseId: trialMetaData.trialSiteResponseId,
							};
							if (trialTreatmentData.length > 0) {
								trialTreatmentData = _.map(trialTreatmentData, (data) => {
									return (data / dataNormalization) * getDataNormalizationMultiplier(sao.dataNormalizationId);
								});
								const average = _.meanBy(trialTreatmentData);
								siteObject.average = average;

								siteObject.excluded = false;
								_.find(
									trialTreatmentAllFailures,
									(t) => t.trialId === trial.trialId && t.assessmentId === sao.assessmentId,
								).failed = false;
							}
							//Filter out trials with no selected assessment data
							else {
								siteObject.average = null;
							}
							trialDataTemp.push(siteObject);
						}
					}
					siteData = siteData.concat(trialDataTemp);
				});
			});
		});

		//-- Adjust excluded flags for all failed plots
		let failures = _.filter(trialTreatmentAllFailures, (t) => t.failed);
		_.map(failures, (f) => {
			//-- Find matching trial-assessment data
			_.map(
				_.filter(siteData, (s) => s.assessmentId === f.assessmentId && s.trialId === f.trialId),
				(fd) => {
					fd.allFailed = true;
				},
			);
		});

		setSiteSpecificData(siteData);
	}

	function getDataNormalizationMultiplier(dataNormalizationId) {
		const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
			"value",
			dataNormalizationId,
		]).text.toLowerCase();

		switch (selectedDataNormalizationName) {
			case "% mean":
			case "% control":
			case "% commercial":
			case "% gsp":
				return 100;
			case "none":
			case "rank order":
			default:
				//-- No modifications to none, just display raw data
				return 1;
		}
	}

	function getDataNormalizationModifier(trialData, assessmentId, dataNormalizationId) {
		const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
			"value",
			dataNormalizationId,
		]).text.toLowerCase();
		switch (selectedDataNormalizationName) {
			case "% mean":
				return getPercentMean(trialData, assessmentId);
			case "% control":
			case "% commercial":
			case "% gsp":
				return getPercentByTreatmentType(trialData, assessmentId, dataNormalizationId);
			case "none":
			case "rank order":
			default:
				//-- No modifications to none, just display raw data
				return 1;
		}
	}

	function getPercentMean(trialData, assessmentId) {
		const assessment = _.find(assessmentOptions, (ao) => {
			return ao.value === assessmentId;
		});
		const data = _.map(
			_.filter(trialData, (td) => {
				return (
					td.excluded !== true &&
					(td.groundDatasetAssessmentValue !== null ||
						td.curveModelAnalysisValue !== null ||
						td.standCountAnalysisResultValue !== null)
				);
			}),
			assessment.isStandCount === String(true)
				? "standCountAnalysisResultValue"
				: assessment.isgrounddata == String(true)
					? "groundDatasetAssessmentValue"
					: "curveModelAnalysisValue",
		);
		const mean = _.meanBy(data);
		return mean;
	}

	function getPercentByTreatmentType(trialData, assessmentId, dataNormalizationId) {
		const assessment = _.find(assessmentOptions, (ao) => {
			return ao.value === assessmentId;
		});
		const treatmentTypeId = _.find(dataNormalizationOptions, (dno) => {
			return dno.value === dataNormalizationId;
		}).treatmenttypeid;
		const trialTreatment = _.find(treatmentData, (td) => {
			return td.treatmentTypeId === treatmentTypeId;
		});
		const data = _.map(
			_.filter(trialData, (td) => {
				return (
					td.trialTreatmentId === trialTreatment?.trialTreatmentId &&
					td.excluded !== true &&
					(td.groundDatasetAssessmentValue !== null ||
						td.curveModelAnalysisValue !== null ||
						td.standCountAnalysisResultValue !== null)
				);
			}),
			assessment.isStandCount === String(true)
				? "standCountAnalysisResultValue"
				: assessment.isgrounddata == String(true)
					? "groundDatasetAssessmentValue"
					: "curveModelAnalysisValue",
		);
		const mean = _.meanBy(data);
		return mean;
	}

	function updateCompiledData(isForUncheckingTrial, updatedTrialsChecked = null) {
		let averageData = [];
		if (siteSpecificData.length > 0) {
			for (var i = 1; i <= reduxProtocolData.treatments; ++i) {
				const treatment = _.find(treatmentData, { trialTreatmentId: i });

				let updatedSiteData = _.map(siteSpecificData, (ssd) => {
					const treatment = _.find(treatmentsChecked, (tc) => {
						return tc.trialTreatmentId === ssd.trialTreatmentId;
					});

					ssd.treatmentChecked = treatment.treatmentChecked;
					return ssd;
				});

				//Filter out trials with no plot data
				updatedSiteData = _.filter(updatedSiteData, (tda) => {
					return tda?.plots.length > 0;
				});

				//for each treatment we need to get each assessment
				const treatmentAssessmentDataAverages = _.filter(updatedSiteData, (sd) => {
					return (
						sd.trialTreatmentId === i &&
						_.find(treatmentsChecked, (tc) => {
							return tc.trialTreatmentId === sd.trialTreatmentId;
						})?.treatmentChecked !== false &&
						sd.trialChecked !== false
					);
				});

				const treatmentAssessmentDataAveragesForStrikeThrough = _.filter(updatedSiteData, (sd) => {
					return sd.trialTreatmentId === i && sd.trialChecked !== false;
				});

				//Building a list of treatments with missing treatments in the trials. This will help determine which treatment needs an info icon
				let treatmentGroup = _.groupBy(siteSpecificData, "trialTreatmentId");
				let maxNumberOfObjects = _.max(Object.values(treatmentGroup).map((a) => a.length));
				let treatmentsMissingInTrials = [];
				let checkedAssessmentIds = _.map(
					_.filter(configuredAssessments, (assessment) => assessment.checked),
					"assessmentId",
				);
				let checkedTrialIds = _.map(
					_.filter(updatedTrialsChecked === null ? trialsChecked : updatedTrialsChecked, (tc) => tc.trialChecked),
					"trialId",
				);
				_.map(treatmentGroup, (tg) => {
					let filteredTreatmentGroup = _.filter(
						tg,
						(t) => checkedAssessmentIds.includes(t.assessmentId) && checkedTrialIds.includes(t.trialId),
					);
					if (
						tg.length != maxNumberOfObjects ||
						_.some(filteredTreatmentGroup, (t) => {
							return t.excluded === true && !t.allFailed;
						})
					) {
						treatmentsMissingInTrials.push(tg[0].trialTreatmentId);
					}
				});

				let compiledTreatment = {
					trialTreatmentId: i,
					treatmentTypeName: treatment.treatmentTypeName,
					treatmentName: treatment.name,
					assessmentData: _.map(treatmentAssessmentDataAverages, (tada) => {
						return {
							fieldId: tada.fieldId,
							assessmentId: tada.assessmentId,
							plannedTimingId: tada.plannedTimingId, //this will be null for all growth curve data
							assessmentAverage: tada.average,
							unitName: tada.unitName,
							plots: tada.plots,
						};
					}),
					assessmentDataForStrikeThrough: _.map(treatmentAssessmentDataAveragesForStrikeThrough, (tada) => {
						return {
							fieldId: tada.fieldId,
							assessmentId: tada.assessmentId,
							plannedTimingId: tada.plannedTimingId, //this will be null for all growth curve data
							assessmentAverage: tada.average,
							unitName: tada.unitName,
							plots: tada.plots,
						};
					}),
					treatmentChecked: _.find(updatedSiteData, (ssd) => {
						return ssd.trialTreatmentId === i;
					})?.treatmentChecked,
					trialId: _.find(siteSpecificData, (ssd) => {
						return ssd.trialTreatmentId === i;
					})?.trialId,
					flightDate: _.find(siteSpecificData, (ssd) => {
						return ssd.trialTreatmentId === i;
					})?.flightDate,
					hasMissingTreatments: treatmentsMissingInTrials.includes(i),
				};
				averageData.push(compiledTreatment);
			}
			setLoading(false);
			setIsUpdatingChange(false);
			setCompiledData(averageData);

			if (!isForUncheckingTrial) {
				getCompiledMeanComparisonData(averageData);
			}
		} else {
			setNoData(true);
		}
	}

	async function getCompiledMeanComparisonData(averageData = null) {
		setCompiledMeanComparisonsRunning(true);
		let allPlotData = [];
		let dataToRemove = [];

		_.map(averageData ?? compiledData, (d) => {
			if (_.find(treatmentsChecked, (tc) => tc.trialTreatmentId === d.trialTreatmentId)?.treatmentChecked) {
				_.map(d.assessmentData, (ad) => {
					let isAssessmentChecked = _.find(configuredAssessments, {
						assessmentId: ad.assessmentId,
						plannedTimingId: ad.plannedTimingId,
					})?.checked;

					//-- If any list of plots is empty, assessment failed and we want to remove the entire treatment from getting clds
					if (isAssessmentChecked) {
						if (ad.plots.length > 0) {
							_.map(ad.plots, (p) => {
								allPlotData.push({ ...p, plannedTimingId: ad.plannedTimingId });
							});
						} else {
							dataToRemove.push({ assessmentId: ad.assessmentId, trialTreatmentId: d.trialTreatmentId });
						}
					}
				});
			}
		});

		allPlotData = _.filter(
			allPlotData,
			(apd) =>
				_.findIndex(
					dataToRemove,
					(dtr) => dtr.assessmentId === apd.assessmentId && dtr.trialTreatmentId === apd.trialTreatmentId,
				) === -1,
		);

		allPlotData = _.orderBy(allPlotData, (a) => [
			a.assessmentId,
			a.plannedTimingId,
			a.trialId,
			a.trialTreatmentId,
			a.plotReplicate,
		]);

		let groupedPlotData = _.groupBy(allPlotData, (a) => `${a.assessmentId} - ${a.plannedTimingId}`);
		let dataToPass = [];

		let updatedCompiledTukeyData = _.cloneDeep(compiledTukeyData);

		_.map(groupedPlotData, (g) => {
			dataToPass.push(g);
		});

		getSingleTrialAucMeanComparisonData();

		if (_.uniqBy(allPlotData, "trialId")?.length > 1) {
			if (dataToPass.length > 0) {
				await Promise.all(
					_.map(dataToPass, async (plotData) => {
						const accessToken = await getTokenSilently();
						return dispatch(
							statisticsActions.getCompiledTukeyData(
								plotData,
								selectedAlphaOption,
								selectedMeanComparisonOption,
								userAuth.currentClientId,
								accessToken,
							),
						).then((res) => {
							return res;
						});
					}),
				)
					.then((res) => {
						let condensedData = [];
						_.map(res, (r) => {
							_.map(r.data?.protocolData, (pd) => {
								condensedData.push(pd);
							});
						});

						let tukeyData = _.groupBy(condensedData, "assessmentId");

						_.map(tukeyData, (td) => {
							assignTukeyLetters(updatedCompiledTukeyData, td);
						});

						setCompiledTukeyData(updatedCompiledTukeyData);

						setCompiledMeanComparisonsRunning(false);
						setHideLetters(false);

						if (needToUpdateAnalytics) {
							setNeedToUpdateAnalytics(false);
						}
					})
					.catch((err) => {
						console.log(err);
						toast.error("There was an issue getting mean comparison values for the multi-trial datasets");

						setCompiledMeanComparisonsRunning(false);
						setHideLetters(false);

						if (needToUpdateAnalytics) {
							setNeedToUpdateAnalytics(false);
						}
					});
			} else {
				setCompiledTukeyData(updatedCompiledTukeyData);

				setCompiledMeanComparisonsRunning(false);
				setHideLetters(false);

				if (needToUpdateAnalytics) {
					setNeedToUpdateAnalytics(false);
				}
			}
		} else if (allPlotData.length === 0) {
			setCompiledMeanComparisonsRunning(false);
			setHideLetters(false);

			if (needToUpdateAnalytics) {
				setNeedToUpdateAnalytics(false);
			}
		} else {
			toast.error("Cannot run multi-trial mean comparisons when only 1 trial dataset is available");

			setCompiledMeanComparisonsRunning(false);

			if (needToUpdateAnalytics) {
				setNeedToUpdateAnalytics(false);
			}
		}

		toast.success("All data has finished loading.", { toastId: "allFinished" });
	}

	function assignTukeyLetters(updatedCompiledData, tukeyData) {
		let tukeyDataToAdd = _.cloneDeep(tukeyData);
		if (_.every(tukeyData, (td) => td.tukeyLetters === "a")) {
			_.forEach(tukeyDataToAdd, (td) => (td.tukeyLetters = "-"));
		}

		updatedCompiledData[tukeyData[0].assessmentId] = tukeyDataToAdd;
	}

	async function getSingleTrialAucMeanComparisonData() {
		setSingleSiteTukeyRunning(true);
		let uniqueTrials = _.map(_.map(trialData, "trialInfo"), "trialId");

		let allPlotData = [];
		_.map(rawTrialData, (rtd) => {
			allPlotData = [...allPlotData, ...rtd];
		});

		let filteredTreatmentsChecked = _.map(_.filter(treatmentsChecked, "treatmentChecked"), "trialTreatmentId");
		allPlotData = _.filter(allPlotData, (a) => filteredTreatmentsChecked.includes(a.trialTreatmentId));

		const accessToken = await getTokenSilently();
		await Promise.all(
			_.map(uniqueTrials, (td) => {
				const requestData = {
					data: _.map(
						_.filter(allPlotData, (tpsd) => {
							return (
								tpsd.trialId === td &&
								(tpsd.assessmentId === selectedCombinedAssessmentOption.assessmentid ||
									tpsd.curveModelName === selectedCombinedAssessmentOption.assessmentid ||
									((tpsd.analysisName === "Stand Count (LAI)" || tpsd.analysisName === "Stand Count (LAI_M3M)") &&
										tpsd.analysisId === selectedCombinedAssessmentOption.assessmentid)) &&
								(tpsd.analysisTypeId === selectedCombinedAssessmentOption.analysistypeid ||
									(tpsd.analysisTypeId === "00000000-0000-0000-0000-000000000000" &&
										selectedCombinedAssessmentOption.analysistypeid === undefined)) //-- It seems like ground data has no analysis type so we need to check for empty analysis types
							);
						}),
						(tpsd) => {
							let totalAuc =
								tpsd.standCountAnalysisResultValue !== null && tpsd.standCountAnalysisResultValue !== 0
									? tpsd.standCountAnalysisResultValue //-- Make sure this doesn't come to the frontend as 0
									: tpsd.curveModelDataAnalysisId
										? tpsd.curveModelAnalysisValue
										: tpsd.assessmentId
											? tpsd.groundDatasetAssessmentValue
											: tpsd.plotAnalysisResultValue;

							return {
								trialTreatmentId: tpsd.trialTreatmentId,
								plotReplicate: tpsd.plotReplicate,
								totalAuc: totalAuc,
							};
						},
					),
				};

				if (requestData.data?.length > 0) {
					return dispatch(
						statisticsActions.getMeanComparisonForAuc(
							requestData,
							userAuth.currentClientId,
							selectedMeanComparisonOption,
							selectedAlphaOption,
							true,
							accessToken,
						),
					).then((res) => {
						res.trialId = td;

						let allLettersAreA = _.every(res.letteringPairs, ["letter", "a"]);

						_.map(res.letteringPairs, (l) => {
							l.trialId = td;
							l.trialTreatmentId = l.treatmentId;
							l.tukeyLetters = allLettersAreA ? "-" : l.letter;
							l.pValue = res.pValue;
							l.cv = res.cv;
						});
						return res;
					});
				} else {
					return Promise.resolve([]);
				}
			}),
		).then((res) => {
			res = _.filter(res, (r) => r.letteringPairs);

			setSingleTrialAnovaValues(res);

			setSingleSiteTukeyRunning(false);
		});
	}

	function updateCheckedTreatment(value, trialTreatmentId) {
		const updatedTreatmentsChecked = _.map(treatmentsChecked, (tc) => {
			if (tc.trialTreatmentId === trialTreatmentId) {
				tc.treatmentChecked = value;
				tc.dirty = true;
			}

			return tc;
		});
		setTreatmentsChecked(updatedTreatmentsChecked);
		setHideLetters(true);
		setNeedToUpdateAnalytics(true);

		//Update unchecked treatments flag for graying and emptying out cells
		let treatmentCheckedList = [];
		let orderedTreatmentList = _.orderBy(updatedTreatmentsChecked, "trialTreatmentId");
		_.filter(orderedTreatmentList, (utc) => {
			if (utc.treatmentChecked) {
				treatmentCheckedList.push(true);
			} else {
				treatmentCheckedList.push(false);
			}
		});

		if (!_.isEmpty(initialTreatmentCheckedList) && !_.isEqual(initialTreatmentCheckedList, treatmentCheckedList)) {
			setHideUncheckedTreatmentData(true);
		} else if (
			_.isEmpty(initialTreatmentCheckedList) &&
			_.some(updatedTreatmentsChecked, (tcl) => !tcl.treatmentChecked)
		) {
			setHideUncheckedTreatmentData(true);
		} else {
			setHideUncheckedTreatmentData(false);
		}
	}

	function updateCheckedSiteData() {
		let updatedSiteData = _.map(siteSpecificData, (ssd) => {
			const treatment = _.find(treatmentsChecked, (tc) => {
				return tc.trialTreatmentId === ssd.trialTreatmentId;
			});

			ssd.treatmentChecked = treatment.treatmentChecked;
			return ssd;
		});

		updatedSiteData = _.map(updatedSiteData, (usd) => {
			const trial = _.find(trialsChecked, (tc) => {
				return tc.trialId === usd.trialId && usd.fieldId == tc.fieldId;
			});

			usd.trialChecked = trial.trialChecked;
			return usd;
		});

		setSiteSpecificData(updatedSiteData);
	}

	function updateCheckedTrial(value, trialId, silent = false, initialLoad) {
		if (!silent) {
			setTrialFilter(trialFilterOptions.find((opt) => opt.text === "Custom"));
		}

		const updatedTrialsChecked = _.map(trialsChecked, (tc) => {
			if (tc.trialId === trialId) {
				tc.trialChecked = value;
				tc.dirty = true;
			}

			return tc;
		});
		setIsUpdatingChange(false);
		setTrialsChecked(updatedTrialsChecked);

		let updatedInitChecked = initTrialsChecked;
		if (updatedInitChecked[trialId] === undefined) {
			updatedInitChecked[trialId] = value;
			setInitTrialsChecked(updatedInitChecked);
		}
		//Logic to hide the auc letters when trials are unchecked

		if (
			Object.keys(initTrialsChecked).length > 0 &&
			!updatedTrialsChecked.every((t) => t.trialChecked === updatedInitChecked[t.trialId])
		) {
			setHideLetters(true);
		} else {
			setHideLetters(false);
		}
		//Update data summary table averages and colors when a trial is checked/unchecked
		if (updatedTrialsChecked?.length > 0) {
			updateCheckedSiteData();
			updateCompiledData(true, updatedTrialsChecked);
		}
		if (!initialLoad) {
			setNeedToUpdateAnalytics(true);
		}
	}

	const updateAssessmentsWithPreset = () => {
		//-- If none, user is customizing selections or page is just loading
		if (selectedAssessmentPresetOption !== "none") {
			//-- Find matching preset
			let matchingPreset = _.find(presetData, { id: selectedAssessmentPresetOption });

			if (matchingPreset) {
				//-- Turn off all assessments

				//-- Get all assessments to turn on
				let allAssessmentsToCheck = [];
				_.map(matchingPreset.sections, ({ presetAnalyses, presetCurveModels, presetGroundData }) => {
					allAssessmentsToCheck = [...allAssessmentsToCheck, ..._.map(presetAnalyses, "analysisId")];
					allAssessmentsToCheck = [...allAssessmentsToCheck, ...presetCurveModels];
					allAssessmentsToCheck = [...allAssessmentsToCheck, ..._.map(presetGroundData, "groundDataAssessmentId")];
				});

				let nextConfiguredAssessments = _.map(configuredAssessments, (ca) => ({
					...ca,
					checked:
						allAssessmentsToCheck.includes(ca.assessmentId) ||
						_.some(
							allAssessmentsToCheck,
							(presetAssessment) =>
								presetAssessment.curveModelAnalysisId === ca.curveModelAnalysisId &&
								presetAssessment.analysisId === ca.analysisId &&
								presetAssessment.analysisTypeId === ca.analysisTypeId,
						),
				}));
				setConfiguredAssessments(nextConfiguredAssessments);
			}
		}
	};

	async function updateMeanComparisonData() {
		if (_.some(treatmentsChecked, (tc) => tc.dirty) || _.some(trialsChecked, (tc) => tc.dirty)) {
			updateCheckedSiteData();

			let clonedTreatmentsChecked = _.cloneDeep(treatmentsChecked);
			setTreatmentsChecked(
				_.map(clonedTreatmentsChecked, (tc) => {
					tc.dirty = false;
					return tc;
				}),
			);

			setTrialsChecked((prev) =>
				_.map(prev, (tc) => {
					tc.dirty = false;
					return tc;
				}),
			);
		}

		//Reset button color
		setDidCheckedAssessmentsChange(false);
		//Set up unchecked treatments and trials lists for gray shading and data cell emptying
		setHideUncheckedTreatmentData(false);
		setHideLetters(false);
		setInitTrialsChecked({});

		let treatmentCheckedList = [];
		let orderedTreatmentList = _.orderBy(treatmentsChecked, "trialTreatmentId");
		_.filter(orderedTreatmentList, (utc) => {
			if (utc.treatmentChecked) {
				treatmentCheckedList.push(true);
			} else {
				treatmentCheckedList.push(false);
			}
		});

		setInitialTreatmentCheckedList(treatmentCheckedList);

		if (needToUpdateAnalytics) {
			let rawTrialDataByReference = [];
			setCompiledMeanComparisonsRunning(true);
			await getAnalysisResults(rawTrialDataByReference);
		} else {
			updateCompiledData(false);
		}
	}

	const handleSaveMetaTags = (info) => {
		const dataMap = new Map(info.map((i) => [i.trialId, i]));
		setTrialData((prev) =>
			prev.map((td) => {
				const curr = dataMap.get(td.trialInfo.trialId);
				if (curr) {
					return { ...td, trialInfo: curr };
				}
				return td;
			}),
		);
		updateMetaTags(info);
	};

	/**
	 * Handle modal editor's apply callback state mgmt, then close the modal.
	 */
	function onApplySummaryTableEditor(assessmentEdits, assessmentsPresetSelectionEdit) {
		setConfiguredAssessments(assessmentEdits);
		setSelectedAssessmentPresetOption(assessmentsPresetSelectionEdit);
		setHideLetters(true);
		setOpenModalEditor(false);
		setNeedToUpdateAnalytics(true);

		toast.warn("Assessments updated. Please click the Update Analytics button.");
	}

	return noData ? (
		<Segment basic>
			<p>{"We could not find data for this protocol."}</p>
		</Segment>
	) : (
		<Segment basic style={{ padding: 0 }}>
			<Form>
				{!loading ? (
					<>
						<Segment className="ribbonBanner" style={{ margin: "0 -1em" }}>
							<div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
								<Tooltip {...tooltipDefaults} overlay="Change settings of protocol analysis summary table">
									<Button
										icon="settings"
										onClick={() => setOpenModalEditor(true)}
										disabled={compiledMeanComparisonsRunning}
									/>
								</Tooltip>
								Summary Table Selections
								<div>
									<Form.Field inline>
										<label htmlFor="form-mean-comparison">Mean Comparison</label>
										<Popup
											content="A statistical test or p-value adjustment that evaluates differences among all pairs of treatments"
											trigger={<Icon name="info circle" link style={{ marginLeft: -5 }} />}
										/>
									</Form.Field>
									<Form.Select
										id="form-mean-comparison"
										fluid
										options={meanComparisonOptions}
										onChange={(event, { value }) => {
											setSelectedMeanComparisonOption(value);
											setHideLetters(true);
											setNeedToUpdateAnalytics(true);
										}}
										disabled={compiledMeanComparisonsRunning}
										value={selectedMeanComparisonOption}
									/>
								</div>
								<div>
									<Form.Field>
										<label htmlFor="form-alpha-value">Significance/alpha</label>
									</Form.Field>
									<Form.Select
										id="form-alpha-value"
										fluid
										options={alphaOptions}
										onChange={(event, { value }) => {
											setHideLetters(true);
											setSelectedAlphaOption(value);
											setNeedToUpdateAnalytics(true);
										}}
										disabled={compiledMeanComparisonsRunning}
										value={selectedAlphaOption}
									/>
								</div>
								<div>
									<Form.Field>
										<label htmlFor="form-presets">Assessment Presets</label>
									</Form.Field>
									<Form.Select
										id="form-presets"
										fluid
										options={presetOptions}
										onChange={(event, { value }) => {
											setSelectedAssessmentPresetOption(value);
											setHideLetters(true);
											setNeedToUpdateAnalytics(true);
										}}
										disabled={compiledMeanComparisonsRunning}
										value={selectedAssessmentPresetOption}
									/>
								</div>
								<div>
									<Form.Field>
										<label htmlFor="form-assessment">Remove Plot Exclusions</label>
									</Form.Field>
									<DataAnalysisCriteria
										removeDamagedOrExcluded={removeDamagedOrExcluded}
										removeOutliers={removeOutliers}
										outlierType={outlierType}
										setRemoveDamagedOrExcluded={setRemoveDamagedOrExcluded}
										setRemoveOutliers={setRemoveOutliers}
										setOutlierType={setOutlierType}
										setNeedToUpdateAnalytics={setNeedToUpdateAnalytics}
									/>
									<Popup
										trigger={<Icon style={{ marginLeft: 10, verticalAlign: "top" }} name="info circle" />}
										content="When toggled to the 'on' position any regions the user has previously marked as excluded will be discarded from analyses"
									/>
								</div>
								<div>
									<Form.Field>
										<label htmlFor="form-trial-filters">Trial Filters</label>
									</Form.Field>
									<Form.Select
										style={{ maxWidth: 250 }}
										id="form-trial-filters"
										options={trialFilterOptions}
										loading={trialFiltersLoading}
										value={trialFilter?.value}
										onChange={(_, { value, options }) => {
											setTrialFilter(options.find((opt) => opt.value === value));
										}}
									/>
								</div>
								<div style={{ alignSelf: "end" }}>
									<Button
										primary={!(hideLetters || needToUpdateAnalytics || didCheckedAssessmentsChange)}
										negative={hideLetters || needToUpdateAnalytics || didCheckedAssessmentsChange}
										fluid
										style={{ marginBottom: "2px" }}
										floated="left"
										disabled={compiledMeanComparisonsRunning || isEditingMetadata}
										onClick={() => {
											if (needToUpdateAnalytics || didCheckedAssessmentsChange) {
												updateMeanComparisonData();
											}
										}}
										loading={compiledMeanComparisonsRunning}
									>
										Update Analytics
									</Button>
								</div>
							</div>
							<ProtocolSummaryTableEditor
								open={openModalEditor}
								assessmentOptions={configuredAssessments}
								presetData={presetData}
								dataNormalizationOptions={dataNormalizationOptions}
								assessmentPresetOptions={presetOptions}
								selectedAssessmentPresetId={selectedAssessmentPresetOption}
								onCancel={() => setOpenModalEditor(false)}
								onApply={onApplySummaryTableEditor}
							/>
						</Segment>
						<Grid>
							{_.some(configuredAssessments, (sao) => sao.checked) ? (
								<>
									<Grid.Row centered style={{ paddingTop: "1em" }}>
										<Grid.Column>
											<Form.Field style={{ overflow: "auto", flexWrap: "nowrap" }}>
												<ProtocolDataSummaryTable
													compiledData={compiledData}
													tukeyData={compiledTukeyData}
													selectedAssessmentOptions={_.filter(configuredAssessments, (ca) => ca.checked)}
													updateCheckedTreatment={updateCheckedTreatment}
													treatmentsChecked={treatmentsChecked}
													disableCheckBoxes={singleSiteTukeyRunning || compiledMeanComparisonsRunning}
													compiledMeanComparisonsRunning={compiledMeanComparisonsRunning}
													selectedAssessmentPresetOption={selectedAssessmentPresetOption}
													presetData={presetData}
													trialsChecked={trialsChecked}
													hideUncheckedTreatmentData={hideUncheckedTreatmentData}
													hideLetters={hideLetters}
												/>
											</Form.Field>
											<br style={{ clear: "both" }} />
										</Grid.Column>
									</Grid.Row>
								</>
							) : (
								<>
									<Segment style={{ marginTop: 15, marginBottom: 15, width: "100%" }} textAlign="center">
										<h2>{"No assessments selected."}</h2>
									</Segment>
								</>
							)}
							<hr style={{ width: "100%" }} />
							<Grid.Row centered style={{ textAlign: "center" }}>
								<h2>Site-Specific Data</h2>
							</Grid.Row>
							<Grid.Row centered style={{ textAlign: "center" }}>
								<Grid.Column width="3" style={{ width: "25%" }}>
									<Form.Select
										id="form-data-normalization"
										fluid
										options={combinedAssessmentOptions}
										disabled={compiledMeanComparisonsRunning || singleSiteTukeyRunning}
										onChange={(event, { value }) => {
											setSelectedCombinedAssessmentOption(_.filter(combinedAssessmentOptions, { key: value })[0]);
										}}
										value={selectedCombinedAssessmentOption?.key}
									/>
								</Grid.Column>
							</Grid.Row>
							{siteSpecificData?.length > 0 && trialData && (
								<div className="trialTableWrapper">
									<Grid.Row style={{ padding: 0 }}>
										<MetaTagsTable
											trialInfo={trialInfo}
											onSave={handleSaveMetaTags}
											setSaveAction={setSaveAction}
											setIsDirty={setIsDirty}
											setIsEditing={setIsEditingMetadata}
										/>
									</Grid.Row>
									<Grid.Row style={{ padding: 0 }}>
										<ProtocolSiteSpecificSummaryTable
											siteSpecificData={filteredSiteSpecificData}
											updateCheckedTrial={updateCheckedTrial}
											appendedPercent={_.find(configuredAssessments, (sao) => {
												return (
													(sao.assessmentId == selectedCombinedAssessmentOption.assessmentid &&
														sao.plannedTimingId == selectedCombinedAssessmentOption.plannedtimingid) ||
													sao.assessmentId == selectedCombinedAssessmentOption.assessmentid
												);
											})?.dataNormalizationName?.includes("%")}
											disableCheckBoxes={compiledMeanComparisonsRunning || singleSiteTukeyRunning}
											tukeyData={_.flatten(
												_.map(singleTrialAnovaValues, (data) => {
													return data.letteringPairs;
												}),
											)}
											tukeyDataRunning={compiledMeanComparisonsRunning || singleSiteTukeyRunning}
											treatmentsChecked={treatmentsChecked}
											hideUncheckedTreatmentData={hideUncheckedTreatmentData}
											hideLetters={hideLetters}
										/>
									</Grid.Row>
								</div>
							)}
						</Grid>
					</>
				) : (
					<Grid.Row>
						<Grid.Column width="16">
							<Loader active></Loader>
						</Grid.Column>
					</Grid.Row>
				)}
			</Form>
		</Segment>
	);
};

ProtocolSummaryTable.propTypes = {
	assessmentOptions: PropTypes.array,
	plannedTimingOptions: PropTypes.array,
	dataNormalizationOptions: PropTypes.array,
	treatmentData: PropTypes.array,
	trialIds: PropTypes.array,
	dashboardOptions: PropTypes.object,
	meanComparisonOptions: PropTypes.array,
	alphaOptions: PropTypes.array,
	presetOptions: PropTypes.array,
	defaultMeanComparisonOption: PropTypes.string,
	defaultAlphaOption: PropTypes.string,
	presetData: PropTypes.array,
	setIsDirty: PropTypes.func,
	setSaveAction: PropTypes.func,
};

export default ProtocolSummaryTable;
